import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Banner — Alerte si les RCCM / NCC retournés par la DGI diffèrent de ceux stockés en interne.
 *
 * Props:
 *   operationId : id de l'opération (transport_request) ou contract_invoice
 *   targetType  : 'spot' | 'contract' (défaut 'spot')
 *   companyName : pour le PATCH d'adoption
 *   visible     : ne charge l'endpoint que si l'op est certifiée FNE
 */
export default function FneRccmMismatchBanner({
  operationId,
  targetType = 'spot',
  companyName,
  visible = true,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adopting, setAdopting] = useState(null); // field being adopted

  const load = useCallback(async () => {
    if (!operationId || !visible) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/fne/client-mismatch/${operationId}`, {
        params: { targetType },
      });
      setData(data);
    } catch (e) {
      // 404 / not certified → silent
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [operationId, targetType, visible]);

  useEffect(() => { load(); }, [load]);

  const adopt = async (mismatch) => {
    if (!companyName) {
      toast.error('Nom d\'entreprise manquant pour l\'adoption');
      return;
    }
    setAdopting(mismatch.field);
    try {
      const fieldMap = { rccm: 'rccm', ncc: 'compteContribuable' };
      const dbField = fieldMap[mismatch.field];
      await axios.patch(`${API}/admin/companies/${encodeURIComponent(companyName)}/profile`, {
        [dbField]: mismatch.dgi,
      });
      toast.success(`${mismatch.field.toUpperCase()} aligné sur la valeur DGI`, {
        description: `Nouvelle valeur : ${mismatch.dgi}`,
      });
      await load();
    } catch (e) {
      toast.error('Échec de l\'adoption', {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setAdopting(null);
    }
  };

  if (!visible || loading || !data?.hasMismatch) return null;

  return (
    <div className="container mx-auto px-4 pt-3" data-testid="fne-mismatch-banner">
      <div className="bg-orange-50 border-l-4 border-orange-500 p-3 rounded-md space-y-2">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-orange-900 text-sm">
              Incohérence d&apos;identité fiscale détectée — {data.companyName}
            </div>
            <div className="text-xs text-orange-800 mt-1">
              La DGI a renvoyé des valeurs différentes lors de la certification.
              Vérifiez et alignez si nécessaire.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          {data.mismatches.map((m) => (
            <div
              key={m.field}
              className="flex items-center justify-between bg-white border border-orange-200 rounded p-2"
            >
              <div className="text-xs">
                <div className="font-semibold uppercase text-orange-700">{m.field}</div>
                <div className="font-mono">
                  <span className="text-slate-500">DGI :</span>{' '}
                  <span className="text-emerald-700 font-semibold">{m.dgi}</span>
                </div>
                <div className="font-mono">
                  <span className="text-slate-500">Interne :</span>{' '}
                  <span className="text-rose-700 line-through">{m.internal}</span>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => adopt(m)}
                disabled={adopting === m.field}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                data-testid={`fne-mismatch-adopt-${m.field}`}
              >
                {adopting === m.field ? (
                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> ...</>
                ) : (
                  <><CheckCircle2 className="w-3 h-3 mr-1" /> Adopter DGI</>
                )}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
